// Word-level diff used by Purpose version history compare view.
// Pure & testable — no React.

export type DiffSegment = { type: "equal" | "added" | "removed"; text: string };

const tokenize = (s: string): string[] => {
  // Keep whitespace tokens so reassembly is faithful.
  if (!s) return [];
  return s.match(/\s+|\S+/g) ?? [];
};

// Longest Common Subsequence over token arrays. O(n*m) — fine for short
// purpose statements.
function lcs(a: string[], b: string[]): number[][] {
  const n = a.length, m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp;
}

/** Diff two strings by whitespace-separated tokens. Returns one entry per
 *  token marked as equal / added (in `next`) / removed (from `prev`). */
export function diffWords(prev: string, next: string): DiffSegment[] {
  const a = tokenize(prev);
  const b = tokenize(next);
  if (!a.length && !b.length) return [];
  const dp = lcs(a, b);
  const out: DiffSegment[] = [];
  let i = a.length, j = b.length;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      out.unshift({ type: "equal", text: a[i - 1] });
      i--; j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      out.unshift({ type: "removed", text: a[i - 1] });
      i--;
    } else {
      out.unshift({ type: "added", text: b[j - 1] });
      j--;
    }
  }
  while (i > 0) { out.unshift({ type: "removed", text: a[i - 1] }); i--; }
  while (j > 0) { out.unshift({ type: "added", text: b[j - 1] }); j--; }
  return out;
}

/** Has anything actually changed? */
export function hasChanges(prev: string, next: string): boolean {
  return diffWords(prev, next).some((s) => s.type !== "equal");
}
